import React from "react";
import { Divider, Typography, Box, TableContainer, Paper, Table, TableHead, TableRow, TableBody, Grid } from "@mui/material";
import { useTranslation } from 'react-i18next';
import CodeContent from "./CodeContent";
import TablaDinamico from "./TablaDinamico";
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import ExampleCard from "./ExampleCard";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? '#3399bf' : '#576572',
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

function createData(
    tipoAfectacion,
    descripcion,
    stock,
    cantidad,
    unidadMedida,
    precioUnitario,
    descuento,
    ganancia,
    IGV,
    importe
) {
    return { tipoAfectacion, descripcion, stock, cantidad, unidadMedida, precioUnitario, descuento, ganancia, IGV, importe };
}

const EmissionExamples = () => {
    const [t] = useTranslation('global');
    const codeGravado =
        `
"ListaDetalles":[{
"ID": 0,
"ComprobanteID": 0,
"Item": 1,
"TipoProductoCodigo": "",
"UnidadMedidaCodigo": "NIU",
"TipoAfectacionIGVCodigo": "10",
"ProductoCodigo": "Prod00115",
"Descripcion": "PRODUCTO GRAVADO",
"Observacion": "",
"TipoSistemaISCCodigo": "00",
"ISCPorcentaje": 0,
"MontoTributo": 18,
"ISC": 0,
"PrecioUnitario": "118",
"PrecioUnitarioItem": 118,
"Cantidad": 1,
"DescuentoPorcentaje": 0,
"Descuento": 0,
"DescuentoBase": 100,
"DescuentoCargoCodigo": "00",
"DescuentoIndicador": 0,
"ValorUnitario": 100,
"ValorVentaItem": 100,
"ValorVentaItemXML": 100,
"ValorVenta": 100,
"IGV": 18,
"ICBPERSubTotal": 0,
"ICBPER": 0,
"ImporteTotal": 118,
"CargoPorcentaje": 0,
"CargoItem": 0,
"Cargo": 0,
"CargoCargoCodigo": "",
"CargoIndicador": 0,
"Percepcion": 0,
"PercepcionPorcentaje": 0,
"PrecioVenta": 118,
"PrecioCodigo": 3473,
"CantidadUnidadMedida": 1,
"PrecioVentaCodigo": "01",
"ProductoCodigoSUNAT": "39121321",
"Peso": 1,
"PesoTotal": 1
"PrecioCompra": 0,
"PrecioUnitarioNeto": 118,
"ValorUnitarioNeto": 100,
"Stock": 1,
"DescuentoMonto": 0,
"Ganancia": 118,
"IGVNeto": 18,
}]
        `;

        const codeExonerado =
        `
"ListaDetalles":[{
"ID": 0,
"ComprobanteID": 0,
"Item": 1,
"TipoProductoCodigo": "",
"UnidadMedidaCodigo": "NIU",
"TipoAfectacionIGVCodigo": "20",
"ProductoCodigo": "Prod00115",
"Descripcion": "PRODUCTO EXONERADO",
"Observacion": "",
"TipoSistemaISCCodigo": "00",
"ISCPorcentaje": 0,
"MontoTributo": 0,
"ISC": 0,
"PrecioUnitario": "118",
"PrecioUnitarioItem": 118,
"Cantidad": 1,
"DescuentoPorcentaje": 0,
"Descuento": 0,
"DescuentoBase": 118,
"DescuentoCargoCodigo": "01",
"DescuentoIndicador": 0,
"ValorUnitario": 118,
"ValorVentaItem": 118,
"ValorVentaItemXML": 118,
"ValorVenta": 118,
"IGV": 0,
"ICBPERSubTotal": 0,
"ICBPER": 0,
"ImporteTotal": 118,
"CargoPorcentaje": 0,
"CargoItem": 0,
"Cargo": 0,
"CargoCargoCodigo": "",
"CargoIndicador": 0,
"Percepcion": 0,
"PercepcionPorcentaje": 0,
"PrecioVenta": 118,
"PrecioCodigo": 3653,
"CantidadUnidadMedida": 1,
"PrecioVentaCodigo": "01",
"ProductoCodigoSUNAT": "39121321",
"Peso": 1,
"PesoTotal": 1
"PrecioCompra": 1.18,
"PrecioUnitarioNeto": 118,
"ValorUnitarioNeto": 118,
"Stock": 1,
"DescuentoMonto": 0,
"Ganancia": 116.82,
"IGVNeto": 0,
}]
        `;

        const codeInafecto =
        `
"ListaDetalles":[{
"ID": 0,
"ComprobanteID": 0,
"Item": 1,
"TipoProductoCodigo": "",
"UnidadMedidaCodigo": "NIU",
"TipoAfectacionIGVCodigo": "30",
"ProductoCodigo": "Prod00122",
"Descripcion": "PRODUCTO INAFECTO",
"Observacion": "",
"TipoSistemaISCCodigo": "00",
"ISCPorcentaje": 0,
"MontoTributo": 0,
"ISC": 0,
"PrecioUnitario": "118",
"PrecioUnitarioItem": 118,
"Cantidad": 1,
"DescuentoPorcentaje": 0,
"Descuento": 0,
"DescuentoBase": 118,
"DescuentoCargoCodigo": "01",
"DescuentoIndicador": 0,
"ValorUnitario": 118,
"ValorVentaItem": 118,
"ValorVentaItemXML": 118,
"ValorVenta": 118,
"IGV": 0,
"ICBPERSubTotal": 0,
"ICBPER": 0,
"ImporteTotal": 118,
"CargoPorcentaje": 0,
"CargoItem": 0,
"Cargo": 0,
"CargoCargoCodigo": "",
"CargoIndicador": 0,
"Percepcion": 0,
"PercepcionPorcentaje": 0,
"PrecioVenta": 118,
"PrecioCodigo": 3629,
"CantidadUnidadMedida": 1,
"PrecioVentaCodigo": "01",
"ProductoCodigoSUNAT": "39121321",
"Peso": 1,
"PesoTotal": 1
"PrecioCompra": 1.18,
"PrecioUnitarioNeto": 118,
"ValorUnitarioNeto": 118,
"Stock": 1,
"DescuentoMonto": 0,
"Ganancia": 116.82,
"IGVNeto": 0,
}]
        `;

    const rows = [
        createData('"10"', "Producto gravado", "1", "1", "UNIDAD", "118", "0", "118", "18", "118"),
        createData('"20"', "Producto exonerado", "1", "1", "UNIDAD", "118", "0", "118", "0", "118"),
        createData('"30"', "Producto inafecto", "1", "1", "UNIDAD", "118", "0", "118", "0", "118"),
    ];
    const tableHeader = [
        t('content.ejemploEmision.tablaEjemploTipoAfectacioncodigo'), 
        t('content.ejemploEmision.tablaEjemploDescripcion'), 
        t('content.ejemploEmision.tablaEjemploStock'), 
        t('content.ejemploEmision.tablaEjemploCantidad'), 
        t('content.ejemploEmision.tablaEjemploUniMedida'), 
        t('content.ejemploEmision.tablaEjemploPrecioUnitario'), 
        t('content.ejemploEmision.tablaEjemploDescuento'), 
        t('content.ejemploEmision.tablaEjemploGanancia'), 
        t('content.ejemploEmision.tablaEjemploIGV'), 
        t('content.ejemploEmision.tablaEjemploImporte')
    ]
    const ListJsonFacturas = [
        t('content.ejemploEmision.ejemploFacturaItem1'),
        t('content.ejemploEmision.ejemploFacturaItem2'),
        t('content.ejemploEmision.ejemploFacturaItem3'),
        t('content.ejemploEmision.ejemploFacturaItem4'),
        t('content.ejemploEmision.ejemploFacturaItem5'),
        t('content.ejemploEmision.ejemploFacturaItem6'),
        t('content.ejemploEmision.ejemploFacturaItem7'),
        t('content.ejemploEmision.ejemploFacturaItem8'),
        t('content.ejemploEmision.ejemploFacturaItem9'),
        t('content.ejemploEmision.ejemploFacturaItem10'),
        t('content.ejemploEmision.ejemploFacturaItem11'),
        t('content.ejemploEmision.ejemploFacturaItem12'),
        t('content.ejemploEmision.ejemploFacturaItem13'),
        t('content.ejemploEmision.ejemploFacturaItem14'),
        t('content.ejemploEmision.ejemploFacturaItem15'),
        t('content.ejemploEmision.ejemploFacturaItem16'),
    ]
    const ListJsonBoletas = [
        t('content.ejemploEmision.ejemploBoletaItem1'),
        t('content.ejemploEmision.ejemploBoletaItem2'),
        t('content.ejemploEmision.ejemploBoletaItem3'),
        t('content.ejemploEmision.ejemploBoletaItem4'),
        t('content.ejemploEmision.ejemploBoletaItem5'),
        t('content.ejemploEmision.ejemploBoletaItem6'),
        t('content.ejemploEmision.ejemploBoletaItem7'),
        t('content.ejemploEmision.ejemploBoletaItem8'),
        t('content.ejemploEmision.ejemploBoletaItem9'),
        t('content.ejemploEmision.ejemploBoletaItem10'),
        t('content.ejemploEmision.ejemploBoletaItem11'),
        t('content.ejemploEmision.ejemploBoletaItem12'),
        t('content.ejemploEmision.ejemploBoletaItem13'),
        t('content.ejemploEmision.ejemploBoletaItem14'),
        t('content.ejemploEmision.ejemploBoletaItem15'),
    ]
    const ListJsonNotaCredito = [
        t('content.ejemploEmision.ejemploNotaCreditoItem1'),
        t('content.ejemploEmision.ejemploNotaCreditoItem2'),
        t('content.ejemploEmision.ejemploNotaCreditoItem3'),
        t('content.ejemploEmision.ejemploNotaCreditoItem4'),
        t('content.ejemploEmision.ejemploNotaCreditoItem5'),
        t('content.ejemploEmision.ejemploNotaCreditoItem6'),
        t('content.ejemploEmision.ejemploNotaCreditoItem7'),
        t('content.ejemploEmision.ejemploNotaCreditoItem8'),
        t('content.ejemploEmision.ejemploNotaCreditoItem9'),
        t('content.ejemploEmision.ejemploNotaCreditoItem10'),
        t('content.ejemploEmision.ejemploNotaCreditoItem11'),
        t('content.ejemploEmision.ejemploNotaCreditoItem12'),
        t('content.ejemploEmision.ejemploNotaCreditoItem13'),
        t('content.ejemploEmision.ejemploNotaCreditoItem14'),
    ]
    const ListJsonNotaDebito = [
        t('content.ejemploEmision.ejemploNotaDebitoItem1'),
        t('content.ejemploEmision.ejemploNotaDebitoItem2'),
        t('content.ejemploEmision.ejemploNotaDebitoItem3'),
        t('content.ejemploEmision.ejemploNotaDebitoItem4'),
        t('content.ejemploEmision.ejemploNotaDebitoItem5'),
        t('content.ejemploEmision.ejemploNotaDebitoItem6'),
    ]
    const ListJsonGuias = [
        t('content.ejemploEmision.ejemploGuiaItem1'),
        t('content.ejemploEmision.ejemploGuiaItem2'),
        t('content.ejemploEmision.ejemploGuiaItem3'),
        t('content.ejemploEmision.ejemploGuiaItem4'),
        t('content.ejemploEmision.ejemploGuiaItem5'),
        t('content.ejemploEmision.ejemploGuiaItem6'),
        t('content.ejemploEmision.ejemploGuiaItem7'),
        t('content.ejemploEmision.ejemploGuiaItem8'),
        t('content.ejemploEmision.ejemploGuiaItem9'),
        t('content.ejemploEmision.ejemploGuiaItem10'),
        t('content.ejemploEmision.ejemploGuiaItem11'),
        t('content.ejemploEmision.ejemploGuiaItem12'),
    ]
    return (
        <div id='ejemplosemision'>
            <Divider sx={{ height: '70px' }} />
            <Typography variant="h3" gutterBottom id="tiposafectacion">
                {t('content.ejemploEmision.titulo')}
            </Typography>
            <Typography variant="h5" gutterBottom id="">
                {t('content.ejemploEmision.subTituloTiposAfectacion')}
            </Typography>
            <TableContainer component={Paper} >
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ fontWeight: "bold", textTransform: 'uppercase' }} align="left">{t('content.ejemploEmision.tablaTipoAfectacionCodigo')}</StyledTableCell>
                            <StyledTableCell sx={{ fontWeight: "bold", textTransform: 'uppercase' }} align="left">{t('content.ejemploEmision.tablaTipoAfectacionDescripcion')}</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">10</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaGravadoOnerosa')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">11</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaGravadoPremio')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">13</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaGravadoRetiro')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">15</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaGravadoBonificaciones')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">16</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaGravadoTrabajadores')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">20</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaExoneradoOnerosa')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">21</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaExoneradoGratuita')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">30</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaInafectoOnerosa')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">31</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaInafectoBonificacion')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">35</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaInafectoPremio')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">36</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaInafectoPublicidad')}</StyledTableCell>
                        </StyledTableRow>
                        <StyledTableRow
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            <StyledTableCell align="left">40</StyledTableCell>
                            <StyledTableCell align="left">{t('content.ejemploEmision.tablaExportacion')}</StyledTableCell>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <br id="ejemplostipoafectacion"/>
            <br />
            <br />
            <Typography variant="h5" gutterBottom id="">
                {t('content.ejemploEmision.subTituloEjemplos')}
            </Typography>
            <TablaDinamico rows={rows} header={tableHeader} />
            <br id="ejemplosobjectosjson"/>
            <br />
            <br />
            <Typography variant="h5" gutterBottom id="">
                {t('content.ejemploEmision.subTituloObjectoJson')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4} minWidth={300}>
                    <Box display='flex' justifyContent={'center'} flexDirection='column' alignItems='center'>
                        <Typography variant="h6" gutterBottom id="">
                            {t('content.ejemploEmision.jsonGravado')}
                        </Typography>
                        <CodeContent code={codeGravado} languajeCode='json' />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4} minWidth={300}>
                    <Box display='flex' justifyContent={'center'} flexDirection='column' alignItems='center'>
                        <Typography variant="h6" gutterBottom id="">
                            {t('content.ejemploEmision.jsonExonerado')}
                        </Typography>
                        <CodeContent code={codeExonerado} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4} minWidth={300}>
                    <Box display='flex' justifyContent={'center'} flexDirection='column' alignItems='center'>
                        <Typography variant="h6" gutterBottom id="">
                            {t('content.ejemploEmision.jsonInafecto')}
                        </Typography>
                        <CodeContent code={codeInafecto} />
                    </Box>
                </Grid>
            </Grid>
            <br id="ejemplosarchivos"/>
            <br />
            <br />
            <Typography variant="h5" gutterBottom >
                {t('content.ejemploEmision.subTituloArchivosJson')}
            </Typography>
            <Typography variant="body1" gutterBottom id="">
                {t('content.ejemploEmision.descripcionArchivosJson')}
            </Typography>
            <Box display='flex' flexDirection='row' justifyContent='center' flexWrap='wrap' gap='1rem' alignItems='start'>
                <ExampleCard title={t('content.ejemploEmision.ejemploFacturaTitulo')} list={ListJsonFacturas} pathDownload="/content/resources/EJEMPLOS DE ARCHIVOS CON FACTURAS.zip" textDownload={t('content.ejemploEmision.ejemploTextoDescargar')}/>
                <ExampleCard title={t('content.ejemploEmision.ejemploBoletaTitulo')} list={ListJsonBoletas} pathDownload="/content/resources/EJEMPLOS DE ARCHIVOS CON BOLETAS.zip" textDownload={t('content.ejemploEmision.ejemploTextoDescargar')}/>
                <ExampleCard title={t('content.ejemploEmision.ejemploNotaCreditoTitulo')} list={ListJsonNotaCredito} pathDownload="/content/resources/EJEMPLOS DE ARCHIVOS CON NOTA DE CREDITO.zip" textDownload={t('content.ejemploEmision.ejemploTextoDescargar')}/>
                <ExampleCard title={t('content.ejemploEmision.ejemploGuiaTitulo')} list={ListJsonGuias} pathDownload="/content/resources/EJEMPLOS DE ARCHIVOS CON GUIA REMITENTE.zip" textDownload={t('content.ejemploEmision.ejemploTextoDescargar')}/>
                <ExampleCard title={t('content.ejemploEmision.ejemploNotaDebitoTitulo')} list={ListJsonNotaDebito} pathDownload="/content/resources/EJEMPLOS DE ARCHIVOS CON NOTA DE DEBITO.zip" textDownload={t('content.ejemploEmision.ejemploTextoDescargar')}/>
            </Box>
        </div>
    )
}

export default EmissionExamples;